import React, { useEffect, useState, useCallback, useMemo } from "react"
import {
  DataGrid,
  useGridApiRef
} from '@mui/x-data-grid'
import Checkbox from "@mui/material/Checkbox"
import { BlockIcon, CheckIcon } from "../../../../components/Icons/Icons"
import {
  addCreditReport,
  saveCreditProfile,
  deleteDebtByDebtId,
} from "../../../../features/creditReports/creditReports"
import { useDispatch } from "react-redux"
import Button from "../../../../components/Button/Button"
import { useMyContextLoading } from "../../../../components/LoadingContextProvider/LoadingContextProvider"
import { editNewLeadDetails } from "../../../../features/contacts/contacts"
import { showSnackBar } from "../../../../components/Snackbar/Snackbar"
import { MISSING_FIELDS_MESSAGE, NOTHING_SAVED_MESSAGE, NO_DEBTS_PERSISTED_MESSAGE, SAVE_FAILED_MESSAGE, buildCreditSelectPayload, hasBlockingErrors, isSelectionEchoComplete, planCreditSelectSave, validateForm, withAmountDefaults } from "./helper"
import { lockScheduleAndReviewTabs, toSavedCreditProfileIds } from "../helper"
import { useManualDebtRules } from "./useManualDebtRules"
import { formatMoney } from "../../../../utils/amount"
import ManualCreditReportModal from "./ManualCreditReportModal"
import EnrolledDebtSummary from "./EnrolledDebtSummary"
import PopupWithActions from "../../../../components/Popup/PopupWithAction"
import ConfirmModal from "../../../../components/ConfirmModal/ConfirmModal"
import ActionIcon from "../../../../assets/images/icons/ActionDotsIcon.svg"
import UpdateCreditReportFields from "./UpdateCreditReportFields"
import dayjs from "dayjs"
import timezone from "dayjs/plugin/timezone"
import utc from "dayjs/plugin/utc"
import { formatBusinessDate, userTimeZone } from "../../../../utils/date"
import PdfViewerDrawer from '../../../../components/CreditReports/PdfViewerDrawer'
import useViewPdf from '../../../../hooks/useViewPdf'
import ProviderBadge from '../../../../components/CreditReports/ProviderBadge'
import PdfBureauButton from '../../../../components/CreditReports/PdfBureauButton'
import { bureauName } from '../../../../components/CreditScores/copy'
import { LuInfo } from 'react-icons/lu'
import ConfirmDialog from "../../../../components/ConfirmDialog/ConfirmDialog"
import { useUiKit } from "../../../../components/UiKit/UiKitContext"
import { failureOf } from "../../../../utils/errorHelpers"

dayjs.extend(utc)
dayjs.extend(timezone)

// Local styled component removed in favor of global theme overrides

const getCreditScoreColor = (score) => {
  if (!score) return '#6b7280'                     // Gray
  if (score < 580) return '#ef4444'                // Poor (300-579) - Red
  if (score < 670) return '#f97316'                // Fair (580-669) - Orange
  if (score < 740) return '#22c55e'                // Good (670-739) - Green
  if (score < 800) return '#15803d'                // Very Good (740-799) - Dark Green
  return '#14532d'                                 // Exceptional (800-850) - Deep Green
}

const CreditTables = ({
  userId,
  setDisableTabs,
  setSelectedTab,
  debts,
  selectedCreditLines,
  setSelectedCreditLines,
  eligibleAccounts,
  ineligibleAccounts,
  setTriggerFetchDebts,
  triggerFetchDebts,
  reportId,
  setReportId,
  isCreditCheck,
  popupOnRequest = false,
  debtsLoaded,
  setIsCreditCheck,
  setCreditSelectType,
  fetchCreditReport,
  refreshAfterSave,
  lastPull,
  creditScore,
  fileResultStatus,
  coAppFileResultStatus,
  hasCoApplicant,
  onCreditSelectSaved,
  debtSummary = false,
}) => {
  const NO_FILE_REASONS = {
    NoFileReturnedCreditFreeze: {
      label: 'FROZEN',
      detail: 'Report is frozen by the consumer. Ask the consumer to lift the freeze, then click Re-Fetch Credit Report.',
    },
    NoFileReturnedSuppressed: {
      label: 'SUPPRESSED',
      detail: 'Report is suppressed by the bureau. Contact the bureau directly.',
    },
    NoFileReturnedNoHit: {
      label: 'NO HIT',
      detail: 'The bureau has no record of this consumer.',
    },
  }
  const resolveReason = (status) => {
    if (!status) return null
    if (NO_FILE_REASONS[status]) return NO_FILE_REASONS[status]
    if (status.startsWith('NoFileReturned')) {
      return { label: 'UNAVAILABLE', detail: `Report not available (${status}). Contact the bureau if persistent.` }
    }
    return null
  }
  const primaryReason = isCreditCheck === true ? resolveReason(fileResultStatus) : null
  const coAppReason = resolveReason(coAppFileResultStatus)
  const showLastPull = Boolean(lastPull?.bureaus?.length) && isCreditCheck === true
  const lastPullResults = showLastPull
    ? lastPull.bureaus
      .map(({ bureau, status }) => `${bureauName(bureau)} (${resolveReason(status)?.label || 'no credit lines found'})`)
      .join(', ')
    : ''
  const noAutoPullActive = showLastPull && new Date(lastPull.noAutoPullUntil) > new Date()
  const dispatch = useDispatch()
  const manualDebtRules = useManualDebtRules()
  const { triggerLoading } = useMyContextLoading()
  const { confirm: kitConfirm } = useUiKit()
  const apiRef = useGridApiRef()
  const ineligibleApiRef = useGridApiRef()

  const [isFirstModalPage, setIsFirstModalPage] = useState(true)
  const [openDeleteModal, setOpenDeleteModal] = useState({ open: false, debtId: '', userId: '' })
  const [openUpdate, setOpenUpdate] = useState({ open: false, creditLine: null })
  const [anchor, setAnchor] = useState(null)
  const [popupOpen, setPopupOpen] = useState(false)
  const [selectedRowForAction, setSelectedRowForAction] = useState(null)
  const { pdfUrl, pdfOpen, pdfLoading, loadedReportId, openPdf, closePdf } = useViewPdf()

  // Build bureau → reportId map from loaded profiles
  const reportMap = useMemo(() => {
    const map = {}
    const profiles = debts || []
    for (const item of profiles) {
      const bureau = item.sourceType
      const ref = item.creditReport
      const refId = typeof ref === 'object' ? ref?._id : ref
      if (bureau && refId && !map[bureau]) map[bureau] = refId
    }
    return map
  }, [debts])

  const getDefaultDate = () => dayjs().tz(userTimeZone).toDate()

  const [creditReport, setCreditReport] = useState({
    fullName: "",
    reportDate: getDefaultDate(),
    creditScore: ""
  })
  const initialFieldSet = {
    creditReport: reportId,
    accountType: "",
    accountNumber: "",
    openedDate: getDefaultDate(),
    reportedDate: getDefaultDate(),
    closedDate: getDefaultDate(),
    lastPaymentDate: getDefaultDate(),
    accountStatus: "",
    isOpenAccount: "",
    creditorName: "",
    balanceAmount: "",
    pastDueAmount: "",
    creditLimit: "",
  }
  const [reqError, setReqError] = useState('')
  const [fields, setFields] = useState([initialFieldSet])
  const [errors, setErrors] = useState([])
  const [revealErrors, setRevealErrors] = useState(false)
  const [triggerCloseDebtsModal, setTriggerCloseDebtsModal] = useState(false)

  const handleCheckboxChange = useCallback((creditLine) => {
    setSelectedCreditLines((prev) => {
      const exists = prev?.find((line) => line._id === creditLine._id)
      if (exists) {
        return prev.filter((line) => line._id !== creditLine._id)
      }
      return prev ? [...prev, creditLine] : [creditLine]
    })
    setDisableTabs(lockScheduleAndReviewTabs)
  }, [setSelectedCreditLines, setDisableTabs])

  const handleActionClick = (event, creditLine) => {
    setAnchor(event.currentTarget)
    setSelectedRowForAction(creditLine)
    setPopupOpen(true)
  }

  const handleActionClose = () => {
    setAnchor(null)
    setPopupOpen(false)
    setSelectedRowForAction(null)
  }

  const handleEditClick = () => {
    setOpenUpdate({ open: true, creditLine: selectedRowForAction })
    handleActionClose()
  }

  const handleDeleteClick = () => {
    setOpenDeleteModal({
      open: true,
      debtId: selectedRowForAction?._id,
      userId: selectedRowForAction?.user
    })
    handleActionClose()
  }

  const removeDebt = async () => {
    triggerLoading(true)
    try {
      if (openDeleteModal?.debtId?.length > 0) {
        const { debtId, userId } = openDeleteModal
        const response = await dispatch(deleteDebtByDebtId({ debtId, userId }))

        if (deleteDebtByDebtId.fulfilled.match(response)) {
          setSelectedCreditLines((prev) => (prev || []).filter((line) => line._id !== debtId))
          setDisableTabs(lockScheduleAndReviewTabs)
          setTriggerFetchDebts(!triggerFetchDebts)
          showSnackBar("success", "The debt has been deleted")
        } else {
          // A rejected thunk's payload is a plain string, so the previous
          // `payload?.data?.message` guard silently swallowed every failure.
          showSnackBar("error", response?.payload || "Failed to delete debt")
        }
      }
    } catch (error) {
      console.error('Error deleting debt:', error)
      showSnackBar("error", "Failed to delete debt")
    } finally {
      triggerLoading(false)
    }
  }

  const removeDebtOrThrow = async () => {
    const { debtId, userId } = openDeleteModal
    const response = await dispatch(deleteDebtByDebtId({ debtId, userId }))
    if (!deleteDebtByDebtId.fulfilled.match(response)) {
      throw failureOf(response, "Failed to delete debt")
    }
    setSelectedCreditLines((prev) => (prev || []).filter((line) => line._id !== debtId))
    setDisableTabs(lockScheduleAndReviewTabs)
    setTriggerFetchDebts(!triggerFetchDebts)
    showSnackBar("success", "The debt has been deleted")
  }

  const createCreditReport = async () => {
    triggerLoading(true)
    try {
      const response = await dispatch(addCreditReport({ userId, data: creditReport }))
      const creditReportId = response?.payload?.data?._id
      setReportId(creditReportId)
      setIsFirstModalPage(false)

      await updateNewLeadDetails({
        postData: {
          creditSelect: {
            creditReport: creditReportId
          }
        }
      })
    } catch (error) {
      showSnackBar("error", "Failed to create credit report")
    } finally {
      triggerLoading(false)
    }
  }

  const createDebts = async () => {
    if (hasBlockingErrors(errors)) {
      setRevealErrors(true)
      showSnackBar("error", MISSING_FIELDS_MESSAGE)
      return
    }

    triggerLoading(true)
    try {
      const response = await dispatch(saveCreditProfile({
        userId,
        data: fields?.map(field => withAmountDefaults({ ...field, creditReport: reportId }))
      }))

      if (response?.error) {
        showSnackBar("error", response?.error?.errors[0]?.message)
      }

      const saved = response?.payload?.data

      if (Array.isArray(saved) && saved.length === 0) {
        showSnackBar("error", NOTHING_SAVED_MESSAGE)
      } else if (saved) {
        setFields([])
        setRevealErrors(false)
        setTriggerCloseDebtsModal(prev => !prev)
        await (refreshAfterSave || fetchCreditReport)()
      }
    } catch (error) {
      showSnackBar("error", "Failed to create debts")
    } finally {
      triggerLoading(false)
    }
  }

  const handleInputChange = (field, value) => {
    setCreditReport((prevData) => ({
      ...prevData,
      [field]: value,
    }))
  }

  const updateNewLeadDetails = async ({ postData, silentSuccess = false }) => {
    try {
      const response = await dispatch(editNewLeadDetails({ userId, data: postData }))
      if (response.error) {
        showSnackBar("error", response?.payload || SAVE_FAILED_MESSAGE)
      } else if (!silentSuccess) {
        showSnackBar("success", "Contact successfully updated")
      }
      return response
    } catch (error) {
      showSnackBar("error", SAVE_FAILED_MESSAGE)
      return { error }
    }
  }

  const handleSaveAndProceed = async () => {
    const { ids: selectedProfileIds, error: saveError } = planCreditSelectSave({ selectedCreditLines, debts })

    if (saveError) {
      showSnackBar("error", saveError)
      return
    }

    triggerLoading(true)
    try {
      const response = await updateNewLeadDetails({
        silentSuccess: true,
        postData: {
          creditSelect: buildCreditSelectPayload({ isCreditCheck, reportId, selectedProfileIds, popupOnRequest })
        }
      })

      if (response?.error) return

      const persistedIds = toSavedCreditProfileIds(response?.payload?.data?.creditSelect?.selectedCreditProfiles)

      if (!isSelectionEchoComplete({ sentIds: selectedProfileIds, persistedIds })) {
        showSnackBar("error", NO_DEBTS_PERSISTED_MESSAGE)
        return
      }

      showSnackBar("success", "Contact successfully updated")
      onCreditSelectSaved?.(persistedIds)
      setDisableTabs((prev) => ({ ...prev, programPlan: false }))
      setSelectedTab("program-plan")
    } finally {
      triggerLoading(false)
    }
  }

  const changeCreditCheckType = () => {
    setIsCreditCheck(null)
    setCreditSelectType('')
  }

  const getColumns = () => [
    {
      field: 'select',
      headerName: '',
      width: 20,
      sortable: false,
      renderCell: (params) => (
        <Checkbox
          checked={!!selectedCreditLines?.find((line) => line._id === params.row._id)}
          onChange={() => handleCheckboxChange(params.row)}
          sx={{
            "&.Mui-checked": {
              color: "text.primary",
            },
          }}
        />
      ),
      headerCheckbox: false,
    },
    {
      field: 'creditorName',
      headerName: 'Creditor',
      flex: 1,
      sortable: false,
      renderCell: (params) => (
        <div className="flex flex-col gap-1 w-full h-full py-2">
          <div className="flex items-start w-full">
            <span className="flex-1 break-words text-wrap line-clamp-3 min-w-0 w-full break-all leading-[1.2] hyphens-auto">
              {params.row?.creditorName || params.row?.bankAccount}
            </span>
            {/* Provider badge */}
            <ProviderBadge sourceType={params.row?.sourceType} />
            <div className="relative group flex-shrink-0 mt-0.5">
              <LuInfo size={16} className="text-text-secondary cursor-help" />
              <div
                className="absolute z-50 w-64 p-3 text-sm font-normal bg-paper border border-divider rounded-lg opacity-0 pointer-events-none group-hover:opacity-100 group-hover:pointer-events-auto transition-all duration-200 top-[-120px] right-0 min-w-[256px]"
              >
                <div
                  className="absolute w-3 h-3 bg-paper border-b border-r border-divider transform rotate-45 bottom-[-6px] right-4"
                />
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span className="text-text-secondary ">Account Condition:</span>
                    <span
                      className="font-semibold text-text-primary text-right text-wrap line-clamp-3">{params.row?.accountCondition?.description || 'N/A'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-text-secondary">Payment Status:</span>
                    <span
                      className="font-semibold text-text-primary text-right text-wrap line-clamp-3">{params.row?.paymentStatus?.description || 'N/A'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-text-secondary">Credit Limit:</span>
                    <span
                      className="font-semibold text-text-primary text-right text-wrap line-clamp-3">{params.row?.creditLimit ? formatMoney(params.row.creditLimit) : 'N/A'}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
          {params.row?.originalCreditorName && (
            <div className="text-text-secondary text-[12px]">
              Original: {params.row.originalCreditorName}
            </div>
          )}
          <div className="text-text-secondary text-[12px]">
            {params.row?.terms && ` (Terms ${params.row.terms})`}
          </div>
        </div>
      ),
    },
    {
      field: 'accountNumber',
      headerName: 'Account Number',
      flex: 1,
      sortable: false,
      renderCell: (params) => (
        <div className="w-full text-wrap line-clamp-3 break-all leading-[1.2] hyphens-auto">
          {params.row?.accountNumber || 'N/A'}
        </div>
      ),
    },
    {
      field: 'accountType',
      headerName: 'Account Type',
      flex: 1,
      sortable: false,
      renderCell: (params) => (
        <div className="w-full text-wrap line-clamp-4" >
          {params.row?.accountType?.description || 'N/A'}
        </div>
      ),
    },
    {
      field: 'accountStatus',
      headerName: 'Account Status',
      flex: 1,
      sortable: false,
      renderCell: (params) => (
        <div className="w-full text-wrap line-clamp-4">
          {params.row?.accountStatus?.description || 'N/A'}
        </div>
      ),
    },
    {
      field: 'balanceAmount',
      headerName: 'Balance Owed',
      flex: 1,
      sortable: false,
      renderCell: (params) => (
        formatMoney(params.row?.balanceAmount)),
    },
    {
      field: 'actions',
      headerName: '',
      width: 20,
      sortable: false,
      renderCell: (params) => (
        <div
          className="cursor-pointer p-2 flex justify-center items-center"
          onClick={(e) => handleActionClick(e, params.row)}
        >
          <img src={ActionIcon} alt="actions" className="w-4 h-4" />
        </div>
      ),
    },
  ]

  const eligibleData = eligibleAccounts || []

  const ineligibleData = ineligibleAccounts || []

  useEffect(() => {
    if (reqError.length > 0) {
      showSnackBar("error", reqError.replace("RequestError: ", "").trim())
      setReqError('')
    }
  }, [reqError])

  useEffect(() => {
    if (reportId) setIsFirstModalPage(false)
  }, [reportId])

  useEffect(() => {
    validateForm({ fields, setErrors, rules: manualDebtRules })
  }, [fields, manualDebtRules])

  return (
    <div className="space-y-6 w-full">
      {debtsLoaded && (
        <ManualCreditReportModal
          isFirstModalPage={isFirstModalPage}
          isManualMode={isCreditCheck === false}
          popupOnRequest={popupOnRequest}
          debts={debts}
          creditReport={creditReport}
          errors={errors}
          fields={fields}
          setFields={setFields}
          setErrors={setErrors}
          handleInputChange={handleInputChange}
          createCreditReport={createCreditReport}
          createDebts={createDebts}
          initialFieldSet={initialFieldSet}
          showFieldErrors={true}
          revealErrors={revealErrors}
          triggerCloseModal={triggerCloseDebtsModal}
          rules={manualDebtRules}
        />
      )}

      {(primaryReason || coAppReason) && (
        <div className="border border-error-main bg-error-light rounded-2xl px-6 py-4 text-sm">
          <div className="font-semibold text-error-main mb-1">Credit report unavailable</div>
          <div className="text-text-secondary space-y-1">
            {primaryReason && <div>Applicant ({primaryReason.label}): {primaryReason.detail}</div>}
            {coAppReason && <div>Co-Applicant ({coAppReason.label}): {coAppReason.detail}</div>}
          </div>
        </div>
      )}

      {showLastPull && (
        <div className="text-sm text-text-secondary">
          {noAutoPullActive
            ? `Last pulled ${formatBusinessDate(lastPull.at)}: ${lastPullResults}. No automatic re-pull before ${formatBusinessDate(lastPull.noAutoPullUntil)} — use Re-Fetch Credit Report to pull now.`
            : `Last pulled ${formatBusinessDate(lastPull.at)}: ${lastPullResults}. Use Re-Fetch Credit Report to pull now.`}
        </div>
      )}

      <div className="bg-surface border border-divider shadow-lg rounded-2xl pb-6 w-full">
        <div className="flex items-center justify-between py-[18px] px-[32px]">
          <div className="font-bold text-xl-20 flex items-center">
            <CheckIcon />
            <div className="ml-2">Eligible Credit Lines</div>
          </div>
          <div className="flex items-center gap-4">
            {primaryReason ? (
              <div className="text-sm text-text-secondary">
                Credit Score: <span className="font-semibold text-primary-static-error-600">{primaryReason.label}</span>
              </div>
            ) : creditScore ? (
              <div className="text-sm text-text-secondary">
                Credit Score: <span className="font-semibold" style={{ color: getCreditScoreColor(creditScore) }}>{creditScore}</span>
              </div>
            ) : null}
            {isCreditCheck && (
              <PdfBureauButton reportMap={reportMap} openPdf={openPdf} pdfLoading={pdfLoading} />
            )}
            {isCreditCheck && (!debts || debts.length === 0) && (
              <Button onClick={changeCreditCheckType} size="sm" type="outlined">
                Change Credit Check Type
              </Button>
            )}
            {(isCreditCheck || hasCoApplicant) && (
              <Button onClick={fetchCreditReport} size="sm" type="outlined">
                Re-Fetch Credit Report
              </Button>
            )}
          </div>
        </div>

        <DataGrid
          sx={{
            border: 'none',
            '& .MuiDataGrid-columnHeaders': {
              '.MuiButtonBase-root[aria-haspopup="menu"]': {
                display: 'none'
              }
            },
            '& .MuiDataGrid-cell': {
              overflow: 'visible !important',
              position: 'relative',
            },
            '& .MuiDataGrid-virtualScroller': {
              minHeight: eligibleData.length === 0 ? 300 : '',
              overflow: 'visible !important',
            },
            '& .MuiDataGrid-main': {
              overflow: 'visible !important',
            },
            '& .MuiDataGrid-viewport': {
              overflow: 'visible !important',
            },
            '& .MuiDataGrid-renderingZone': {
              overflow: 'visible !important',
            },
            '& .MuiDataGrid-window': {
              overflow: 'visible !important',
            }
          }}
          rowHeight={80}
          columns={getColumns(true)}
          rows={eligibleData.map(item => ({ ...item, id: item._id }))}
          apiRef={apiRef}
          slots={{
            noRowsOverlay: () => (
              <div className="py-[20px] text-center text-text-secondary text-lg-18">
                At this time, there are no existing eligible profiles available.
              </div>
            ),
          }}
          hideFooterPagination={true}
          disableRowSelectionOnClick
          disableColumnSelector
          checkboxSelection={false}
        />
      </div>

      {ineligibleData.length > 0 && (
        <div className="bg-surface border border-divider shadow-lg rounded-2xl pb-6">
          <div className="flex items-center justify-between py-[18px] px-[32px]">
            <div className="font-bold text-xl-20 flex items-center gap-2">
              <BlockIcon />
              Ineligible credit lines
            </div>
          </div>

          <DataGrid
            sx={{
              border: 'none',
              '& .MuiDataGrid-columnHeaders': {
                '.MuiButtonBase-root[aria-haspopup="menu"]': {
                  display: 'none'
                }
              },
              '& .MuiDataGrid-cell': {
                overflow: 'visible !important',
                position: 'relative',
              },
              '.MuiDataGrid-virtualScroller': {
                minHeight: ineligibleData.length === 0 ? 200 : '',
                overflow: 'visible !important'
              },
              '& .MuiDataGrid-main': {
                overflow: 'visible !important',
              },
              '& .MuiDataGrid-viewport': {
                overflow: 'visible !important',
              },
              '& .MuiDataGrid-renderingZone': {
                overflow: 'visible !important',
              },
              '& .MuiDataGrid-window': {
                overflow: 'visible !important',
              }
            }}
            rowHeight={80}
            columns={getColumns(false)}
            rows={ineligibleData.map(item => ({ ...item, id: item._id }))}
            apiRef={ineligibleApiRef}
            slots={{
              noRowsOverlay: () => (
                <div className="py-[20px] text-center text-text-secondary text-lg-18">
                  The following credit lines are not supported by our relief programs.
                  Ineligible includes secured debts like mortgages, car loans & student loans.
                </div>
              ),
            }}
            hideFooterPagination={true}
            disableRowSelectionOnClick
            disableColumnSelector
            checkboxSelection={false}
          />
        </div>
      )}

      <div className={debtSummary ? "flex justify-between items-center" : "flex justify-end"}>
        {debtSummary && <EnrolledDebtSummary selectedCreditLines={selectedCreditLines} />}
        <Button
          width="min-w-[156px]"
          disabled={!selectedCreditLines?.length}
          onClick={handleSaveAndProceed}
        >
          Save and Proceed
        </Button>
      </div>

      {kitConfirm ? (
        <ConfirmDialog
          open={openDeleteModal.open}
          onClose={() => setOpenDeleteModal({ ...openDeleteModal, open: false })}
          title="Delete this debt?"
          impact={["The debt is removed from this contact.", "This cannot be undone."]}
          confirmLabel="Delete debt"
          tone="danger"
          onConfirm={removeDebtOrThrow}
        />
      ) : (
        <ConfirmModal
          title="Are you sure to Delete this debt?"
          functionButton={removeDebt}
          modalButtonTitle="Delete"
          open={openDeleteModal.open}
          setOpen={(v) => setOpenDeleteModal({ ...openDeleteModal, open: v })}
          titleClasses="text-left ml-0"
        >
          You will not be able to cancel this action
        </ConfirmModal>
      )}

      <UpdateCreditReportFields
        open={openUpdate.open}
        setOpen={(v) => setOpenUpdate({ ...openUpdate, open: v })}
        creditLine={openUpdate.creditLine}
        setTriggerFetchDebts={setTriggerFetchDebts}
      />

      <PopupWithActions
        anchor={anchor}
        open={popupOpen}
        handleClose={handleActionClose}
      >
        <div className="flex flex-col">
          <div
            onClick={handleEditClick}
            className="cursor-pointer border-b rounded-md border-b-divider p-[14px] text-text-primary hover:bg-surface"
          >
            Edit
          </div>
          <div
            onClick={handleDeleteClick}
            className="cursor-pointer p-[14px] rounded-md text-primary-static-error-600 hover:bg-surface"
          >
            Delete
          </div>
        </div>
      </PopupWithActions>

      <PdfViewerDrawer
        open={pdfOpen}
        pdfUrl={pdfUrl}
        reportId={loadedReportId}
        onClose={closePdf}
      />
    </div>
  )
}

export default CreditTables